import React, { useState, useEffect } from 'react';

interface MediaItem {
  id: string;
  src: string;
  type: 'image' | 'video';
  photographer?: string;
  alt?: string;
  animation?: string;
  category?: string;
}

interface GeoSensitiveSlideshowProps {
  apiKey: string;
  interval?: number;
}

type Region = 'africa' | 'europe' | 'north_america' | 'south_america' | 'oceania' | 'asia' | 'world';

/** Derive a broad region from the browser's IANA timezone — no network call needed. */
function getRegion(): Region {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.toLowerCase() ?? '';
    if (tz.startsWith('africa/')) return 'africa';
    if (tz.startsWith('europe/') || tz.startsWith('atlantic/')) return 'europe';
    if (tz.startsWith('australia/') || tz.startsWith('pacific/')) return 'oceania';
    if (tz.startsWith('asia/') || tz.startsWith('indian/')) return 'asia';
    if (tz.startsWith('america/')) {
      // Rough South-America detection by city names
      const sa = ['bogota','lima','santiago','sao_paulo','buenos_aires','caracas',
                  'guayaquil','la_paz','montevideo','asuncion','manaus','recife',
                  'fortaleza','belem','cuiaba','campo_grande','porto_alegre',
                  'cayenne','paramaribo','georgetown'];
      return sa.some(c => tz.includes(c)) ? 'south_america' : 'north_america';
    }
    if (tz.startsWith('us/') || tz.startsWith('canada/')) return 'north_america';
  } catch {}
  return 'world';
}

type CategorySet = Array<{ name: string; queries: string[] }>;

const REGIONAL_QUERIES: Record<Region, CategorySet> = {
  africa: [
    { name: 'family',    queries: ['african family smiling together', 'black family celebrating outdoors', 'african grandmother grandchild portrait', 'african parents playing with children'] },
    { name: 'friends',   queries: ['african friends laughing together', 'black friends group celebrating', 'african young people smiling outdoors', 'south african friends reunion'] },
    { name: 'work',      queries: ['african business team smiling meeting', 'african woman professional office', 'black doctor talking to patient', 'african teacher with students classroom'] },
    { name: 'milestone', queries: ['african wedding celebration smiling', 'african graduation ceremony', 'african family newborn baby', 'black people party celebration dancing'] },
    { name: 'memory',    queries: ['african elderly couple smiling together', 'black grandparent grandchild', 'african family reunion hug', 'african family looking at photos'] },
  ],

  europe: [
    { name: 'family',    queries: ['european family smiling together', 'european grandmother grandchild', 'parents children laughing outdoors europe', 'european family dinner table'] },
    { name: 'friends',   queries: ['european friends laughing cafe', 'friends smiling city europe', 'european couple smiling outdoors', 'european young people celebrating'] },
    { name: 'work',      queries: ['european business colleagues smiling office', 'european woman professional meeting', 'european doctor patient smiling', 'european teacher students'] },
    { name: 'milestone', queries: ['european wedding couple smiling', 'european graduation ceremony', 'european family newborn baby', 'european friends toasting celebration'] },
    { name: 'memory',    queries: ['european elderly couple holding hands', 'european grandparent grandchild reading', 'european family photo album', 'european friends reunion hugging'] },
  ],

  north_america: [
    { name: 'family',    queries: ['american family smiling backyard', 'diverse american family together', 'north american parents children laughing', 'american family road trip'] },
    { name: 'friends',   queries: ['american friends laughing outdoors', 'diverse friends group smiling', 'american couple smiling together', 'american friends park celebration'] },
    { name: 'work',      queries: ['diverse american team office smiling', 'american woman professional meeting', 'american doctor patient smiling', 'american teacher students classroom'] },
    { name: 'milestone', queries: ['american wedding couple smiling', 'american college graduation', 'american family newborn baby', 'american friends birthday party'] },
    { name: 'memory',    queries: ['american elderly couple smiling porch', 'american grandparent grandchild', 'american family thanksgiving dinner', 'american family reunion barbecue'] },
  ],

  south_america: [
    { name: 'family',    queries: ['latin family smiling together', 'south american family celebration', 'latin grandmother grandchild', 'latin american parents children outdoors'] },
    { name: 'friends',   queries: ['latin friends laughing together', 'south american friends group smiling', 'latin american couple smiling', 'latin people celebrating together'] },
    { name: 'work',      queries: ['latin american business team smiling', 'south american woman professional', 'latin american doctor patient', 'latin american teacher students'] },
    { name: 'milestone', queries: ['latin american wedding celebration', 'south american graduation ceremony', 'latin family newborn baby', 'south american carnival people smiling'] },
    { name: 'memory',    queries: ['latin elderly couple smiling', 'south american grandparent grandchild', 'latin family reunion outdoors', 'latin american family photo together'] },
  ],

  oceania: [
    { name: 'family',    queries: ['australian family smiling beach', 'australian family outdoors together', 'new zealand family portrait', 'australian parents children playing'] },
    { name: 'friends',   queries: ['australian friends laughing outdoors', 'australian friends beach smiling', 'new zealand friends group smiling', 'australian people celebration'] },
    { name: 'work',      queries: ['australian business team smiling office', 'australian woman professional', 'australian doctor patient smiling', 'australian teacher students'] },
    { name: 'milestone', queries: ['australian wedding couple beach', 'australian graduation ceremony', 'australian family newborn baby', 'australian friends backyard celebration'] },
    { name: 'memory',    queries: ['australian elderly couple smiling', 'australian grandparent grandchild', 'australian family reunion barbecue', 'australian family looking at photos'] },
  ],

  asia: [
    { name: 'family',    queries: ['asian family smiling together', 'asian grandmother grandchild portrait', 'asian parents children laughing', 'asian family celebration lunar new year'] },
    { name: 'friends',   queries: ['asian friends laughing together', 'asian friends group smiling', 'asian couple smiling outdoors', 'asian young people celebration'] },
    { name: 'work',      queries: ['asian business team smiling office', 'asian woman professional meeting', 'asian doctor talking to patient', 'asian teacher students classroom'] },
    { name: 'milestone', queries: ['asian wedding couple smiling', 'asian graduation ceremony', 'asian family newborn baby', 'asian people festival celebration dancing'] },
    { name: 'memory',    queries: ['asian elderly couple smiling together', 'asian grandparent grandchild', 'asian family reunion dinner', 'asian family looking at photos'] },
  ],

  world: [
    { name: 'family',    queries: ['happy family smiling together', 'grandmother hugging grandchild', 'parents playing with children outdoors', 'family laughing at dinner'] },
    { name: 'friends',   queries: ['friends laughing together outdoors', 'couple smiling at each other', 'group of friends celebrating', 'people hugging at reunion'] },
    { name: 'work',      queries: ['diverse team smiling in meeting', 'woman presenting to colleagues', 'doctor talking to patient smiling', 'teacher with students classroom'] },
    { name: 'milestone', queries: ['graduate throwing cap celebration', 'bride and groom smiling wedding', 'family holding baby newborn', 'people toasting at celebration'] },
    { name: 'memory',    queries: ['grandmother showing photos to grandchild', 'family looking at photo album together', 'elderly couple holding hands smiling', 'parent reading bedtime story child'] },
  ],
};

const GeoSensitiveSlideshow: React.FC<GeoSensitiveSlideshowProps> = ({
  apiKey,
  interval = 10000
}) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(true);

  const filterContent = (items: any[]) => {
    const blocked = [
      'nude', 'naked', 'erotic', 'porn', 'nsfw', 'xxx', 'explicit',
      'lingerie', 'sex ', 'sexual', 'gore', 'bloody', 'murder',
      'gun ', 'guns ', 'weapon', 'violence', 'drug ', 'drugs ',
      'marijuana', 'cocaine',
    ];
    return items.filter((item: any) => {
      const text = `${item.alt || ''} ${item.photographer || ''}`.toLowerCase();
      return !blocked.some(w => text.includes(w));
    });
  };

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const kenBurnsEffects = [
          'ken-burns-zoom-in', 'ken-burns-zoom-out',
          'ken-burns-pan-left', 'ken-burns-pan-right',
          'ken-burns-zoom-pan-left', 'ken-burns-zoom-pan-right',
        ];

        const region = getRegion();
        const allCategories = REGIONAL_QUERIES[region];

        const now = new Date();
        const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
        const hourOfDay = now.getHours();
        const dateSeed = dayOfYear * 24 + hourOfDay;

        // Cache key includes region so travellers get correct images
        const cacheKey = `wsslide_v4_${region}_${dateSeed}`;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed: MediaItem[] = JSON.parse(cached);
            if (parsed.length > 0) {
              setMedia(parsed);
              setLoading(false);
              return;
            }
          }
        } catch {}

        const seededRandom = (seed: number) => {
          const x = Math.sin(seed++) * 10000;
          return x - Math.floor(x);
        };

        const selectedCategories = [...allCategories].sort(() => seededRandom(dateSeed) - 0.5);
        const queriesByCategory: Array<{ category: string; query: string }> = [];
        selectedCategories.forEach((cat, idx) => {
          const picked = [...cat.queries].sort(() => seededRandom(dateSeed + idx) - 0.5)[0];
          if (picked) queriesByCategory.push({ category: cat.name, query: picked });
        });

        const randomPage = 1 + Math.floor(seededRandom(dateSeed + 100) * 20);

        const fetchResults = await Promise.all(
          queriesByCategory.map(({ category, query }, i) => {
            const pageNum = randomPage + Math.floor(seededRandom(dateSeed + i) * 5);
            return fetch(
              `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=10&page=${pageNum}&orientation=landscape&content_filter=high&order_by=relevant`,
              { headers: { Authorization: `Client-ID ${apiKey}` } }
            )
              .then(r => r.json())
              .then(data => {
                const images: MediaItem[] = filterContent(
                  (data.results || [])
                    .filter((p: any) => p.width > p.height)
                    .map((p: any) => ({
                      id: `img-${p.id}`,
                      src: `${p.urls.raw}&w=1280&q=72&fm=webp&auto=format`,
                      type: 'image' as const,
                      photographer: p.user.name,
                      alt: p.alt_description || p.description || '',
                      animation: kenBurnsEffects[Math.floor(seededRandom(dateSeed + i + 200) * kenBurnsEffects.length)],
                      category,
                    }))
                );
                return images;
              })
              .catch(() => [] as MediaItem[]);
          })
        );

        const allImages: MediaItem[] = fetchResults.flat();

        // Prefer images whose alt text mentions people
        const humanKeywords = [
          'people', 'person', 'man', 'woman', 'child', 'family', 'friend',
          'couple', 'group', 'team', 'crowd', 'human', 'face', 'portrait',
          'boy', 'girl', 'baby', 'kid', 'adult', 'senior', 'elder',
          'mother', 'father', 'daughter', 'son', 'grandmother', 'grandfather',
          'smiling', 'laughing', 'hugging', 'holding', 'sitting', 'standing',
          'walking', 'talking', 'playing', 'celebrating', 'dancing', 'working',
        ];
        const humanImages = allImages.filter(img => {
          const text = `${img.alt || ''}`.toLowerCase();
          return humanKeywords.some(kw => text.includes(kw));
        });

        const uniqueImages = Array.from(
          new Map((humanImages.length > 0 ? humanImages : allImages).map(item => [item.id, item])).values()
        );

        // One image per category
        const shuffled = [...uniqueImages].sort(() => seededRandom(dateSeed + 300) - 0.5);
        const seen = new Set<string>();
        const finalMedia: MediaItem[] = [];
        for (const item of shuffled) {
          const key = item.category || 'uncategorized';
          if (seen.has(key)) continue;
          seen.add(key);
          finalMedia.push(item);
        }

        console.log(`🎨 [${region}] Loaded ${finalMedia.length} images`);

        const result = finalMedia.length > 0 ? finalMedia : allImages.slice(0, 5);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(result)); } catch {}

        result.slice(0, 2).forEach(item => {
          const img = new Image();
          img.src = item.src;
        });

        setMedia(result);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch media from Unsplash:', error);
        setLoading(false);
      }
    };

    fetchMedia();
  }, [apiKey]);

  useEffect(() => {
    if (media.length < 2) return;
    const img = new Image();
    img.src = media[(currentIndex + 1) % media.length].src;
  }, [currentIndex, media]);

  useEffect(() => {
    if (media.length < 2) return;
    const timer = setInterval(() => {
      setNextIndex((currentIndex + 1) % media.length);
      setShowNext(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % media.length);
        setShowNext(false);
      }, 1500);
    }, interval);
    return () => clearInterval(timer);
  }, [media.length, interval, currentIndex]);

  if (loading || !media.length) {
    return (
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          width: '100vw', height: '100vh',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 40%, #1e1b4b 100%)',
          transform: 'translateZ(0)',
          contain: 'layout style paint',
        }}
      />
    );
  }

  const currentMedia = media[currentIndex];
  const nextMedia = media[nextIndex] || media[0];

  const renderMedia = (item: MediaItem, isFirst = false) => (
    <img
      key={item.id}
      src={item.src}
      alt={item.alt || item.photographer || ''}
      role="presentation"
      aria-hidden="true"
      className={`w-full h-full object-cover ${item.animation || 'ken-burns-zoom-in'}`}
      loading={isFirst ? 'eager' : 'lazy'}
      fetchPriority={isFirst ? 'high' : 'low'}
      decoding={isFirst ? 'sync' : 'async'}
      width="1920"
      height="1080"
      style={{
        animationDuration: `${interval}ms`,
        animationTimingFunction: 'ease-in-out',
        animationFillMode: 'forwards',
        aspectRatio: '16/9',
      }}
    />
  );

  return (
    <div
      className="fixed inset-0 overflow-hidden bg-slate-900 -z-10 pointer-events-none"
      style={{
        width: '100vw', height: '100vh',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        contain: 'layout style paint',
      }}
    >
      <div className="absolute inset-0">
        {renderMedia(currentMedia, currentIndex === 0)}
      </div>
      <div
        className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${showNext ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDuration: '1.5s' }}
      >
        {renderMedia(nextMedia, false)}
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent"></div>
      <div className="absolute bottom-4 left-4 text-white/70 text-xs font-medium">
        <p>© {currentMedia.photographer || 'Unsplash'}</p>
      </div>
    </div>
  );
};

export default GeoSensitiveSlideshow;
